import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import Footer from '../sections/Footer';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { blogPosts } from '../../data/blogPosts';

const { FiClock, FiUser, FiCalendar, FiArrowLeft, FiShare2, FiShield } = FiIcons;

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Image */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/30 via-gray-900/60 to-gray-900 z-10" />
        <img 
          src={post.imageUrl} 
          alt={post.title} 
          className="w-full h-full object-cover"
        />
      </div>

      <div className="container mx-auto px-6 relative z-20 -mt-32">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Back Link */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-300 hover:text-[#ffc700] mb-6 transition-colors font-medium backdrop-blur-sm bg-black/30 px-4 py-2 rounded-full">
            <SafeIcon icon={FiArrowLeft} /> Back to Blog
          </Link>

          {/* Article Header */}
          <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-2xl mb-12">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                {post.category}
              </span>
              <span className="text-gray-400 text-sm flex items-center gap-1">
                <SafeIcon icon={FiCalendar} /> {post.date}
              </span>
              <span className="text-gray-400 text-sm flex items-center gap-1">
                <SafeIcon icon={FiClock} /> {post.readTime}
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center justify-between border-t border-gray-700 pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#ffc700] to-orange-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiUser} className="text-black" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">{post.author}</p>
                  <p className="text-gray-400 text-xs">Scam Awareness Advocate</p>
                </div>
              </div>
              <button className="text-gray-400 hover:text-[#ffc700] transition-colors">
                <SafeIcon icon={FiShare2} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Article Content */}
          <div className="bg-gray-900 rounded-2xl p-8 md:p-12 border border-gray-800">
            <div 
              className="prose prose-lg prose-invert max-w-none 
                prose-headings:text-white prose-headings:font-bold prose-headings:mb-4 prose-headings:mt-8
                prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-[#ffc700] prose-a:no-underline hover:prose-a:underline
                prose-ul:list-disc prose-ul:pl-6 prose-ul:text-gray-300 prose-ul:mb-6
                prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-gray-300 prose-ol:mb-6
                prose-li:mb-2
                prose-strong:text-white
                prose-lead:text-xl prose-lead:text-gray-200 prose-lead:font-medium"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* CTA Box */}
          <div className="mt-12 bg-gradient-to-br from-purple-900 to-gray-900 p-8 rounded-2xl border border-pink-500/30 text-center">
            <SafeIcon icon={FiShield} className="text-4xl text-[#ffc700] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Need Scam Support?</h3>
            <p className="text-gray-300 mb-6">Connect with certified advocates at AVAH Outreach Group for free.</p>
            <Link to="/scam-assistance" className="inline-block bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-3 rounded-full font-bold transition-all transform hover:scale-105 shadow-lg">
              Get Help Now
            </Link>
          </div>

        </motion.div>
      </div>

      <div className="mt-20">
        <Footer />
      </div>
    </div>
  );
};

export default BlogPostPage;